import { useEffect, useRef, useState } from "react";
import type { Call } from "../lib/calls";
import {
  CALL_SCREEN_START, applyCallEvent, callEndLine, callInitials, callStatus, endCall, firstNameOf, togetherMs,
  type CallRoom, type CallScreenState, type CallSide,
} from "../lib/callRoom";
import { makeCallRoom } from "../lib/callRooms";
import { Icon } from "./Icon";

/* THE CALL SCREEN ON THE WEB (session 2 of `docs/CALLS-BRIEF.md`): 1:1 video against a STAND-IN room. The web coach's twin of
 * the phone's `CallScreen` — the other person's picture fills the window, mine is a small tile, the name with one line under
 * it (clock · length · topic), and three controls: mute, camera, end (a laptop has one camera — no flip).
 *
 * ⚠️ THE SCREEN KNOWS A ROOM ONLY THROUGH `CallRoom`; `makeCallRoom` picks it (k12). ⚠️ ESCAPE DOES NOT END A CALL — a reflex
 * key must not hang up on a client; End is the one way out. The surface is DARK in both themes: it is a media surface (the
 * other person's picture), like FaceTime's. Toggled controls take the system's reserved toggled look (white fill, dark glyph);
 * End is the destructive red (Apple HIG Buttons, read 2026-10-12). */

export const JOIN_CALL = "ht:join-call";
export type JoinCallDetail = { call: Call; name: string };

/** THE ONE HOST — mounted beside each page's `CallWindow`; it opens the screen when `joinCall` says so and leaves the call's
 *  ONE chat line through the page's `useCalls` write (`ended`). */
export function CallHost({ onEnded }: { readonly onEnded: (c: Call, line: string) => Promise<void> }) {
  const [open, setOpen] = useState<JoinCallDetail | null>(null);
  useEffect(() => {
    const onJoin = (e: Event) => { setOpen((cur) => cur ?? (e as CustomEvent<JoinCallDetail>).detail); };
    window.addEventListener(JOIN_CALL, onJoin);
    return () => window.removeEventListener(JOIN_CALL, onJoin);
  }, []);
  if (open === null) return null;
  return <CallScreen call={open.call} otherName={open.name} asCoach
                     onClose={async (line) => { if (line !== null) await onEnded(open.call, line); setOpen(null); }} />;
}

/** One frame: the room draws INTO the element (Daily: a <video> on the track; the stand-in a drawn picture). */
function Frame({ room, side }: { readonly room: CallRoom; readonly side: CallSide }) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = ref.current;
    if (el === null) return;
    return room.attach(side, el);
  }, [room, side]);
  return <div ref={ref} className="call-frame" />;
}

function CallScreen({ call, otherName, asCoach, onClose }: {
  readonly call: Call; readonly otherName: string; readonly asCoach: boolean;
  readonly onClose: (line: string | null) => Promise<void>;
}) {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const firstRef = useRef<HTMLButtonElement>(null);
  // ⚠️ THE ROOM IS MADE BY THE EFFECT, never once per component: React mounts twice in development, and a room made once
  // and LEFT by the first unmount is a closed room for the second — the client never arrived (render finding, 2026-10-12).
  const [room, setRoom] = useState<CallRoom | null>(null);
  const [s, setS] = useState<CallScreenState>(CALL_SCREEN_START);
  const [now, setNow] = useState(() => Date.now());
  const ending = useRef(false);

  useEffect(() => {
    const el = dialogRef.current;
    if (el !== null && !el.open) el.showModal();
    firstRef.current?.focus();
    const r = makeCallRoom(call);
    setRoom(r); setS(CALL_SCREEN_START);
    const off = r.listen((e) => setS((cur) => applyCallEvent(cur, e, Date.now())));
    void r.join();
    const tick = setInterval(() => setNow(Date.now()), 1000);
    return () => { off(); clearInterval(tick); void r.leave(); };
    // The call is fixed for the screen's life (the host keys nothing else in).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function end() {
    if (ending.current) return;
    ending.current = true;
    const t = Date.now();
    const done = endCall(s, t);
    setS(done);
    await room?.leave();
    await onClose(callEndLine(togetherMs(done, t) / 1000, asCoach));
  }

  const otherHere = s.phase === "live";
  const other = firstNameOf(otherName);
  const status = callStatus(s, togetherMs(s, now) / 1000, call.minutes, call.topic, other);
  return (
    <dialog ref={dialogRef} className="call-screen" aria-label={`Call with ${otherName}`}
            onCancel={(e) => { e.preventDefault(); }}>
      <div className="call-stage">
        {room !== null && otherHere && s.otherCameraOn
          ? <Frame room={room} side="other" />
          : (
            <div className="call-empty">
              <span className="call-initials" aria-hidden="true">{callInitials(otherName)}</span>
              {otherHere && <span className="call-empty-word">Camera off</span>}
            </div>
          )}
        <div className="call-scrim is-top" aria-hidden="true" />
        <div className="call-scrim is-bottom" aria-hidden="true" />

        <header className="call-head">
          <h2 className="call-name">
            {otherName}
            {otherHere && !s.otherMicOn && (
              <span className="call-muted" role="img" aria-label={`${other} is muted`}><Icon name="mic-off" size={15} /></span>
            )}
          </h2>
          <p className="call-status" aria-live="polite">{status}</p>
        </header>

        <div className={s.cameraOn ? "call-self" : "call-self is-off"} aria-label={s.cameraOn ? "You" : "You, camera off"} role="img">
          {!s.cameraOn ? <Icon name="video-off" size={22} /> : room !== null && <Frame room={room} side="me" />}
          {!s.micOn && <span className="call-self-muted" aria-hidden="true"><Icon name="mic-off" size={13} /></span>}
        </div>

        <div className="call-bar" role="toolbar" aria-label="Call controls">
          <button ref={firstRef} type="button" className={s.micOn ? "call-control" : "call-control is-on"}
                  aria-label="Mute" aria-pressed={!s.micOn}
                  onClick={() => { const on = !s.micOn; setS({ ...s, micOn: on }); void room?.setMicrophone(on); }}>
            <Icon name={s.micOn ? "mic" : "mic-off"} size={22} />
          </button>
          <button type="button" className={s.cameraOn ? "call-control" : "call-control is-on"}
                  aria-label="Stop video" aria-pressed={!s.cameraOn}
                  onClick={() => { const on = !s.cameraOn; setS({ ...s, cameraOn: on }); void room?.setCamera(on); }}>
            <Icon name={s.cameraOn ? "video" : "video-off"} size={22} />
          </button>
          <button type="button" className="call-control is-end" aria-label="End call" onClick={() => { void end(); }}>
            <Icon name="phone-down" size={24} />
          </button>
        </div>
      </div>
    </dialog>
  );
}
